/**
 * Payment Allocation & Tallying Utilities
 * Implements First-In, First-Out (FIFO) invoice distribution from oldest to newest.
 */

/**
 * Calculates FIFO allocations across open invoices based on total received amount.
 * 
 * @param {Array<Object>} openInvoices - List of unpaid or partially paid invoice objects
 * @param {number|string} totalReceived - Total lump-sum payment received from customer
 * @returns {{
 *   allocations: Array<{
 *     invoiceId: string,
 *     invoiceNo: string,
 *     invoiceDate: string,
 *     grandTotal: number,
 *     alreadyPaid: number,
 *     balanceDue: number,
 *     allocatedAmount: number,
 *     remainingBalance: number,
 *     nextStatus: string
 *   }>,
 *   totalApplied: number,
 *   unallocatedAmount: number
 * }}
 */
export function calculateFifoAllocations(openInvoices = [], totalReceived = 0) {
  const received = Math.max(0, Number(totalReceived) || 0);

  // Sort invoices chronologically (oldest invoiceDate or createdAt first)
  const sorted = [...openInvoices].sort((a, b) => {
    const aDate = new Date(a.invoiceDate || a.createdAt || 0).getTime();
    const bDate = new Date(b.invoiceDate || b.createdAt || 0).getTime();
    if (aDate !== bDate) return aDate - bDate;
    return String(a.invoiceNo || "").localeCompare(String(b.invoiceNo || ""), undefined, { numeric: true });
  });

  let remaining = received;
  let totalApplied = 0;

  const allocations = sorted.map((inv) => {
    const grandTotal = Number(inv.grandTotal || 0);
    const alreadyPaid = Number(inv.paid !== undefined ? inv.paid : (inv.paidAmount || 0));
    const balanceDue = Number(inv.balance !== undefined ? inv.balance : Math.max(0, grandTotal - alreadyPaid));

    // Allocate up to balanceDue
    const allocated = Math.round(Math.max(0, Math.min(balanceDue, remaining)) * 100) / 100;
    remaining = Math.round(Math.max(0, remaining - allocated) * 100) / 100;
    totalApplied = Math.round((totalApplied + allocated) * 100) / 100;

    const remainingBalance = Math.round(Math.max(0, balanceDue - allocated) * 100) / 100;
    const nextStatus = remainingBalance === 0 ? "Paid" : (alreadyPaid + allocated > 0 ? "Partial" : "Unpaid");

    return {
      invoiceId: inv.id,
      invoiceNo: inv.invoiceNo || "N/A",
      invoiceDate: inv.invoiceDate || "",
      isNonGst: !!(inv.isNonGst || String(inv.invoiceType || "").toLowerCase() === "normal"),
      grandTotal,
      alreadyPaid,
      balanceDue,
      allocatedAmount: allocated,
      remainingBalance,
      nextStatus
    };
  });

  return {
    allocations,
    totalApplied,
    unallocatedAmount: remaining
  };
}

/**
 * Validates manual per-row allocation adjustments against total received amount.
 * 
 * @param {Array<Object>} allocations 
 * @param {number|string} totalReceived 
 * @returns {{ isValid: boolean, error?: string, totalApplied: number, unallocatedAmount: number }}
 */
export function validateManualAllocations(allocations = [], totalReceived = 0) {
  const received = Math.max(0, Number(totalReceived) || 0);
  let totalApplied = 0;

  for (const alloc of allocations) {
    const amount = Number(alloc.allocatedAmount || 0);
    if (amount < 0) {
      return {
        isValid: false,
        error: `Allocated amount for invoice ${alloc.invoiceNo} cannot be negative.`,
        totalApplied: 0,
        unallocatedAmount: received
      };
    }
    if (amount > alloc.balanceDue + 0.01) {
      return {
        isValid: false,
        error: `Allocated amount (₹${amount}) exceeds balance due (₹${alloc.balanceDue}) for invoice ${alloc.invoiceNo}.`,
        totalApplied: 0,
        unallocatedAmount: received
      };
    }
    totalApplied = Math.round((totalApplied + amount) * 100) / 100;
  }

  if (totalApplied > received + 0.01) {
    return {
      isValid: false,
      error: `Total applied (₹${totalApplied}) exceeds received amount (₹${received}).`,
      totalApplied,
      unallocatedAmount: 0
    };
  }

  const unallocatedAmount = Math.round(Math.max(0, received - totalApplied) * 100) / 100;

  return {
    isValid: true,
    totalApplied,
    unallocatedAmount
  };
}
